import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'
import { Spinner } from '@/components/ui/Spinner'
import { formatSortCode, formatAccountNumber, sortCodeError, accountNumberError } from '@/utils/bankDetails'
import api from '@/services/api'

/**
 * Standalone Direct Debit setup page. Sent by an admin to a customer who has a
 * contract on debit order but never completed a Direct Debit (e.g. signed the
 * contract without it). They enter their bank details (validated server-side via
 * Postcoder) and accept the Bacs mandate — no need to re-sign the contract.
 *
 * Reuses the same token-scoped endpoints as the in-signing DD step:
 *   GET  /signing/view/{token}            → consent text + state
 *   POST /signing/{token}/dd-authorization → validate + record the mandate
 */
export default function SetupDirectDebit() {
  const { token } = useParams<{ token: string }>()
  const [dd, setDd] = useState({ account_holder_name: '', sort_code: '', account_number: '', consent: false })
  const [done, setDone] = useState(false)
  const [bank, setBank] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['dd-setup', token],
    queryFn: async () => (await api.get(`/signing/view/${token}`)).data as any,
    enabled: !!token,
    retry: false,
  })

  const mutation = useMutation({
    mutationFn: async () => {
      const r = await api.post(`/signing/${token}/dd-authorization`, {
        sort_code: dd.sort_code.trim(),
        account_number: dd.account_number.trim(),
        account_holder_name: dd.account_holder_name.trim(),
        consent_given: dd.consent,
      })
      return r.data as { success: boolean; bank_name?: string }
    },
    onSuccess: (d) => { setDone(true); setBank(d.bank_name || '') },
  })

  const formValid =
    dd.account_holder_name.trim().length > 1 &&
    dd.sort_code.replace(/\D/g, '').length === 6 &&
    dd.account_number.replace(/\D/g, '').length === 8 &&
    dd.consent

  const shell = (children: React.ReactNode) => (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-6">
        <div className="text-center mb-4">
          <img src="/images/silux_mobile_logo.png" alt="Silux Mobile" className="h-12 w-auto mx-auto mb-3" />
          <p className="text-sm text-gray-500">Direct Debit Setup</p>
        </div>
        {children}
      </Card>
    </div>
  )

  if (isLoading) return shell(<div className="text-center py-8"><Spinner size="lg" /></div>)

  if (error || !data) {
    return shell(
      <Alert variant="error">This Direct Debit link is invalid or has expired. Please contact support.</Alert>
    )
  }

  if (done || data.status === 'already_signed') {
    return shell(
      <div className="text-center py-4">
        <div className="text-green-500 text-4xl mb-2">✅</div>
        <h2 className="text-lg font-semibold text-gray-900">Direct Debit set up</h2>
        <p className="text-sm text-gray-600 mt-1">
          Thank you{bank ? ` — verified with ${bank}` : ''}. Your Direct Debit is now in place; nothing more to do.
        </p>
      </div>
    )
  }

  return shell(
    <>
      <p className="text-sm text-gray-600 mb-4">
        Please enter your bank details below. We verify them securely before setting up your monthly Direct Debit.
      </p>

      <label className="block text-sm font-medium text-gray-700 mb-1">Account holder name <span className="text-red-500">*</span></label>
      <Input value={dd.account_holder_name} onChange={(e) => setDd({ ...dd, account_holder_name: e.target.value })} placeholder="Name on the account" />

      <div className="grid grid-cols-2 gap-3 mt-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sort code <span className="text-red-500">*</span></label>
          <Input value={dd.sort_code} onChange={(e) => setDd({ ...dd, sort_code: formatSortCode(e.target.value) })} placeholder="00-00-00" inputMode="numeric" error={sortCodeError(dd.sort_code)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Account number <span className="text-red-500">*</span></label>
          <Input value={dd.account_number} onChange={(e) => setDd({ ...dd, account_number: formatAccountNumber(e.target.value) })} placeholder="8 digits" inputMode="numeric" error={accountNumberError(dd.account_number)} />
        </div>
      </div>

      {data.dd_consent && (
        <div className="mt-4 bg-gray-50 rounded-lg p-3 max-h-44 overflow-y-auto text-xs text-gray-600 whitespace-pre-line">
          {data.dd_consent.statement}
          {'\n\n'}
          {data.dd_consent.guarantee}
        </div>
      )}

      <label className="flex items-start gap-2 mt-3 text-sm text-gray-800 cursor-pointer">
        <input type="checkbox" className="mt-1 h-4 w-4" checked={dd.consent} onChange={(e) => setDd({ ...dd, consent: e.target.checked })} />
        <span>I confirm I am the account holder and authorise {data.dd_consent?.service_user_name || 'Silux Mobile'} to set up a Direct Debit on this account under the Direct Debit Guarantee.</span>
      </label>

      {mutation.isError && (
        <Alert variant="error" className="mt-3">
          {(mutation.error as any)?.response?.data?.detail || 'Could not set up the Direct Debit. Please check the details and try again.'}
        </Alert>
      )}

      <Button variant="primary" className="w-full mt-4" onClick={() => mutation.mutate()} isLoading={mutation.isPending} disabled={!formValid}>
        Set up Direct Debit
      </Button>
      {!formValid && (
        <p className="text-xs text-gray-500 mt-2 text-center">Enter the account holder, a 6-digit sort code, an 8-digit account number, and tick the box.</p>
      )}
    </>
  )
}
